<template>
  <div
    class="h-full overflow-y-auto flex flex-col custom-scrollbar"
    :class="[elevation.surface.sunken.default]"
  >
    <!-- Logo in top-left corner -->
    <div class="p-s500">
      <div class="flex items-center">
        <img
          :src="hostbentoLogoUrl"
          alt="Hostbento Logo"
          class="rounded-md h-8 w-auto"
        />
        <sub class="text-xs">by Konigle</sub>
      </div>
    </div>

    <!-- Main content centered -->
    <div class="flex-1 flex items-center justify-center p-s400">
      <div class="w-full max-w-md">
        <!-- Heading -->
        <div class="mb-12 text-center">
          <h1 :class="[font.heading.xlarge, colors.text.default, 'mb-s300']">
            Login to hostbento
          </h1>
          <p :class="[font.body.large, colors.text.subtle]">
            Build beautiful websites with AI using your favorite chat app
          </p>
        </div>

        <!-- Login Card -->
        <div
          class="rounded-xl border p-8 shadow-sm"
          :class="[elevation.surface.raised.default, colors.border.default]"
        >
          <!-- Email Input State -->
          <div v-if="!codeSent">
            <div class="mb-s600">
              <p :class="[font.body.default, colors.text.subtle]">
                Enter your email to receive a login code
              </p>
            </div>

            <Form>
              <Field label="Email" :is-required="true">
                <TextField
                  v-model="email"
                  type="email"
                  placeholder="you@example.com"
                  @keyup.enter="handleRequestCode"
                  :disabled="isRequesting || showingRequestCaptcha"
                />
                <HelperMessage
                  >Use your work email if you want to collaborate with teammates
                  in the future</HelperMessage
                >
              </Field>
            </Form>

            <div v-if="errorMessage" class="mt-s400">
              <HelperMessage :variant="'danger'">
                {{ errorMessage }}
              </HelperMessage>
            </div>

            <div class="mt-s600">
              <div
                id="recaptcha-request-code"
                class="g-recaptcha mb-s400"
              ></div>

              <Button
                variant="primary"
                size="large"
                class="w-full"
                :is-disabled="
                  !isEmailValid || isRequesting || showingRequestCaptcha
                "
                :is-loading="isRequesting"
                @click="handleRequestCode"
              >
                {{ isRequesting ? "Sending code..." : "Continue with email" }}
              </Button>
            </div>
          </div>

          <!-- Code Input State -->
          <div v-else>
            <div class="mb-s600">
              <button
                @click="resetToEmail"
                :class="[
                  font.body.small,
                  colors.text.subtle,
                  'hover:underline',
                  'flex',
                  'items-center',
                  'gap-s100',
                  'mb-s500',
                ]"
              >
                <IconArrowLeft :size="16" />
                Back
              </button>

              <h2
                :class="[font.heading.medium, colors.text.default, 'mb-s200']"
              >
                Check your email
              </h2>
              <p :class="[font.body.default, colors.text.subtle]">
                We sent a 6-digit code to
                <strong :class="[colors.text.default]">{{ email }}</strong
                >. Check your spam folder if you don't see it within a few
                minutes.
              </p>
            </div>

            <Form>
              <Field label="Verification code" :is-required="true">
                <TextField
                  v-model="code"
                  type="text"
                  placeholder="000000"
                  maxlength="6"
                  @keyup.enter="handleVerifyCode"
                  :disabled="isVerifying"
                  class="text-center text-xl tracking-widest font-mono"
                />
              </Field>
            </Form>

            <div v-if="errorMessage" class="mt-s400">
              <HelperMessage :variant="'danger'">
                {{ errorMessage }}
              </HelperMessage>
            </div>

            <div class="mt-s600">
              <div id="recaptcha-verify-code" class="g-recaptcha mb-s400"></div>

              <Button
                variant="primary"
                size="large"
                class="w-full"
                :is-disabled="
                  !isCodeValid || isVerifying || showingVerifyCaptcha
                "
                :is-loading="isVerifying"
                @click="handleVerifyCode"
              >
                {{ isVerifying ? "Verifying..." : "Verify and continue" }}
              </Button>
            </div>

            <div class="mt-s500 text-center">
              <button
                @click="handleRequestCode"
                :disabled="isRequesting"
                :class="[
                  font.body.default,
                  colors.text.subtle,
                  'hover:underline',
                  'disabled:opacity-50',
                ]"
              >
                Didn't receive a code? Send again
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-s600 text-center">
          <p :class="[font.body.small, colors.text.subtle]">
            By continuing, you agree to Konigle's
            <Link href="https://konigle.com/terms" target="_blank">
              Terms of Service
            </Link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import hostbentoLogoUrl from "@/assets/img/hostbento-logo.webp";

import { hostbentoAuthApi } from "@/services/api/hostbento";
import { Button } from "@konigle/pixel/components/button";
import { Field, Form, HelperMessage } from "@konigle/pixel/components/form";
import { TextField } from "@konigle/pixel/components/input";
import { Link } from "@konigle/pixel/components/link";
import { colors, elevation, font } from "@konigle/pixel/tokens";
import { IconArrowLeft } from "@tabler/icons-vue";
import { computed, ref } from "vue";

declare global {
  interface Window {
    RECAPTCHA_SITE_KEY: string;
    grecaptcha: {
      ready: (callback: () => void) => void;
      render: (
        container: string | HTMLElement,
        parameters: {
          sitekey: string;
          size: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
        },
      ) => number;
      reset: (widgetId: number) => void;
    };
  }
}

const email = ref("");
const code = ref("");
const codeSent = ref(false);
const isRequesting = ref(false);
const isVerifying = ref(false);
const errorMessage = ref("");
const showingRequestCaptcha = ref(false);
const showingVerifyCaptcha = ref(false);
let requestCaptchaWidgetId: number | null = null;
let verifyCaptchaWidgetId: number | null = null;

// Read next URL from query params
const nextUrl = ref("");
const urlParams = new URLSearchParams(window.location.search);
const nextParam = urlParams.get("next");
if (nextParam) {
  nextUrl.value = decodeURIComponent(nextParam);
}

const isEmailValid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.value.trim());
});

const isCodeValid = computed(() => {
  return code.value.trim().length === 6;
});

const handleRequestCaptchaExpired = () => {
  showingRequestCaptcha.value = false;
  errorMessage.value = "Captcha expired. Please try again.";
  if (requestCaptchaWidgetId !== null) {
    window.grecaptcha.reset(requestCaptchaWidgetId);
  }
};

const handleRequestCaptchaError = () => {
  showingRequestCaptcha.value = false;
  errorMessage.value = "Captcha error. Please try again.";
  if (requestCaptchaWidgetId !== null) {
    window.grecaptcha.reset(requestCaptchaWidgetId);
  }
};

const handleRequestCode = async () => {
  if (!isEmailValid.value || isRequesting.value || showingRequestCaptcha.value)
    return;

  const captchaContainer = document.getElementById("recaptcha-request-code");
  if (window.grecaptcha && window.RECAPTCHA_SITE_KEY && captchaContainer) {
    showingRequestCaptcha.value = true;

    requestCaptchaWidgetId = window.grecaptcha.render(captchaContainer, {
      sitekey: window.RECAPTCHA_SITE_KEY,
      size: "normal",
      callback: handleRequestCodeWithToken,
      "expired-callback": handleRequestCaptchaExpired,
      "error-callback": handleRequestCaptchaError,
    });
  } else {
    console.error("reCAPTCHA not available");
  }
};

const handleRequestCodeWithToken = async (captchaToken: string) => {
  isRequesting.value = true;
  errorMessage.value = "";

  try {
    await hostbentoAuthApi.requestCode({
      email: email.value.trim().toLowerCase(),
      captcha_token: captchaToken,
    });

    codeSent.value = true;
    code.value = "";
  } catch (error) {
    errorMessage.value =
      error instanceof Error
        ? error.message
        : "Failed to send code. Please try again.";
    if (requestCaptchaWidgetId !== null) {
      window.grecaptcha.reset(requestCaptchaWidgetId);
    }
  } finally {
    isRequesting.value = false;
    showingRequestCaptcha.value = false;
  }
};

const handleVerifyCaptchaExpired = () => {
  showingVerifyCaptcha.value = false;
  errorMessage.value = "Captcha expired. Please try again.";
  if (verifyCaptchaWidgetId !== null) {
    window.grecaptcha.reset(verifyCaptchaWidgetId);
  }
};

const handleVerifyCaptchaError = () => {
  showingVerifyCaptcha.value = false;
  errorMessage.value = "Captcha error. Please try again.";
  if (verifyCaptchaWidgetId !== null) {
    window.grecaptcha.reset(verifyCaptchaWidgetId);
  }
};

const handleVerifyCode = async () => {
  if (!isCodeValid.value || isVerifying.value || showingVerifyCaptcha.value)
    return;

  const captchaContainer = document.getElementById("recaptcha-verify-code");
  if (window.grecaptcha && window.RECAPTCHA_SITE_KEY && captchaContainer) {
    showingVerifyCaptcha.value = true;
    verifyCaptchaWidgetId = window.grecaptcha.render(captchaContainer, {
      sitekey: window.RECAPTCHA_SITE_KEY,
      size: "normal",
      callback: handleVerifyCodeWithToken,
      "expired-callback": handleVerifyCaptchaExpired,
      "error-callback": handleVerifyCaptchaError,
    });
  } else {
    console.error("reCAPTCHA not available");
  }
};

const handleVerifyCodeWithToken = async (captchaToken: string) => {
  isVerifying.value = true;
  errorMessage.value = "";

  try {
    const requestData: {
      email: string;
      code: string;
      captcha_token: string;
      next?: string;
    } = {
      email: email.value.trim().toLowerCase(),
      code: code.value.trim(),
      captcha_token: captchaToken,
    };

    if (nextUrl.value) {
      requestData.next = nextUrl.value;
    }

    const response = await hostbentoAuthApi.verifyCode(requestData);

    window.location.href = response.redirect_url;
  } catch (error) {
    errorMessage.value =
      error instanceof Error
        ? error.message
        : "Invalid or expired code. Please try again.";
    code.value = "";
    if (verifyCaptchaWidgetId !== null) {
      window.grecaptcha.reset(verifyCaptchaWidgetId);
    }
  } finally {
    isVerifying.value = false;
    showingVerifyCaptcha.value = false;
  }
};

const resetToEmail = () => {
  codeSent.value = false;
  code.value = "";
  errorMessage.value = "";
  showingVerifyCaptcha.value = false;
  if (verifyCaptchaWidgetId !== null) {
    window.grecaptcha.reset(verifyCaptchaWidgetId);
    verifyCaptchaWidgetId = null;
  }
};
</script>
